/**
 * dispatchCobrancaToProvider
 * --------------------------------------------------------------------------
 * Helper único para disparar uma cobrança (status 'aguardando_emissao') ao
 * provider via edge function `financial-charge` (action 'create_single').
 *
 * Centraliza a chamada para os fluxos:
 *   - src/hooks/useCreateSubscription.ts          (criação de assinatura)
 *   - src/hooks/tenant/useTenantPlansV2.ts        (checkout do tenant)
 *   - src/hooks/wl/useWlTenantPlanBinding.ts      (painel WL)
 *
 * IMPORTANTE: não contém regra de negócio — apenas orquestra o invoke.
 * Toda a lógica de criação/cálculo da cobrança permanece no módulo
 * financeiro (RPCs + edge function).
 *
 * Não-bloqueante por design: falha aqui NÃO desfaz a assinatura/troca de
 * plano; o worker faz retry e o botão "Reenviar ao Provider" segue como
 * fallback manual.
 *
 * CHANGELOG:
 *   v1.0.0 — 2026-06-16 — Extração inicial.
 *   v2.0.0 — 2026-06-17 — Contrato enxuto. Backend (financial-charge v1.8.0)
 *                          passa a carregar valor_total, data_vencimento,
 *                          descricao, observacoes e tipo_pagamento direto do
 *                          banco (fonte única de verdade) usando cobranca_id.
 *                          Removidos do payload: valor_total, data_vencimento,
 *                          descricao, numero_parcelas. Elimina a classe de bug
 *                          em que o client ditava valor divergente do banco.
 *   v2.1.0 — 2026-06-17 — Suporte a `action: 'create_installment'` para
 *                          cobranças parceladas (paridade com create_single).
 *                          Backend (v1.9.0) carrega numero_parcelas via COUNT
 *                          em financeiro_parcelas — fonte única de verdade
 *                          para os 3 tipos: avulsa, parcelada e recorrência.
 */
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("dispatchCobranca");

/**
 * Payload mínimo aceito pela edge function `financial-charge` actions
 * `create_single` (avulsa/recorrência) e `create_installment` (parcelada)
 * a partir da v1.8.0/v1.9.0.
 *
 * O backend carrega valor_total, data_vencimento, descricao, observacoes,
 * tipo_pagamento e numero_parcelas a partir de `cobranca_id` (banco como
 * fonte única de verdade). Esses campos NÃO são mais aceitos no payload.
 */
export interface DispatchCobrancaPayload {
  /** Obrigatório — chave da source-of-truth no banco. */
  cobranca_id: string;
  conta_corrente_id: string;
  crm_customer_id: string;
  /**
   * Action no edge function. Default 'create_single' (cobrança avulsa
   * ou 1ª cobrança de assinatura). Use 'create_installment' para parcelada.
   */
  action?: "create_single" | "create_installment";
  /**
   * Fallback usado pelo backend apenas se a cobrança não tiver
   * `meio_pagamento_id` vinculado. Em fluxos normais é ignorado.
   */
  tipo_pagamento?: string;
  /** Concatenado pelo backend na description do provider. */
  observacoes?: string | null;
  /** Tag textual para rastrear origem nos logs (ex.: 'plan_change_pro_rata'). */
  origin?: string;
}

/**
 * Despacha a cobrança ao provider. Nunca lança — apenas registra warn.
 * Retorna `true` quando o invoke não devolveu erro, `false` em caso de falha.
 */
export async function dispatchCobrancaToProvider(
  payload: DispatchCobrancaPayload,
): Promise<boolean> {
  const action = payload.action ?? "create_single";
  try {
    const { error } = await supabase.functions.invoke("financial-charge", {
      body: {
        action,
        cobranca_id: payload.cobranca_id,
        conta_corrente_id: payload.conta_corrente_id,
        crm_customer_id: payload.crm_customer_id,
        // Campos opcionais — backend usa banco como fonte de verdade.
        ...(payload.tipo_pagamento ? { tipo_pagamento: payload.tipo_pagamento } : {}),
        ...(payload.observacoes !== undefined ? { observacoes: payload.observacoes } : {}),
      },
    });

    if (error) {
      log.warn("Provider retornou erro (será reenviado pelo worker)", {
        action,
        origin: payload.origin,
        cobranca_id: payload.cobranca_id,
        error: error.message,
      });
      return false;
    }
    return true;
  } catch (err) {
    log.warn("Falha de comunicação com provider (será reenviado)", {
      action,
      origin: payload.origin,
      cobranca_id: payload.cobranca_id,
      error: String(err),
    });
    return false;
  }
}
